'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import ConfettiEffect from './ConfettiEffect';
import { useTranslation } from '@/lib/i18n';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName?: string;
  isPackage?: boolean;
}

const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  planName = 'Pro',
  isPackage = false,
}) => {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showButton, setShowButton] = useState(false);

  // Sequential animation effect
  useEffect(() => {
    if (isOpen) {
      // Reset all states
      setShowConfetti(false);
      setShowCard(false);
      setShowTitle(false);
      setShowButton(false);

      // Start animation sequence
      const timers: NodeJS.Timeout[] = [];

      // 1. Start confetti immediately
      setShowConfetti(true);

      // 2. Show card after 500ms
      timers.push(
        setTimeout(() => {
          setShowCard(true);
        }, 500),
      );

      // 3. Show title after 800ms
      timers.push(setTimeout(() => {}, 800));

      // 4. Show title after 1100ms
      timers.push(
        setTimeout(() => {
          setShowTitle(true);
        }, 1100),
      );

      // 5. Show button after 1400ms
      timers.push(
        setTimeout(() => {
          setShowButton(true);
        }, 1400),
      );

      // Stop confetti after 5 seconds
      timers.push(
        setTimeout(() => {
          setShowConfetti(false);
        }, 5000),
      );

      return () => {
        timers.forEach((timer) => clearTimeout(timer));
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Confetti Animation */}
      <ConfettiEffect isActive={showConfetti} particleCount={150} duration={5000} />

      {/* Modal Overlay */}
      <div
        className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 backdrop-blur-xs"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Success Card */}
        <div
          className={`relative transition-all duration-500 transform ${
            showCard ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
          }`}
        >
          <div
            className="relative w-[335px] h-[360px] bg-linear-to-br from-purple-900/90 to-blue-900/90 rounded-3xl py-8 backdrop-blur-xl shadow-2xl border border-purple-500/20"
            style={{
              backgroundImage: 'url(/image/upgrade_complete_bg.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* Badge/Shield Icon */}
            <div className="flex justify-center mb-2">
              <Image
                src="/image/upgrade_complete.png"
                alt={isPackage ? t('Purchase successful') : t('Upgrade completed!')}
                width={130}
                height={130}
                className="object-contain"
              />
            </div>

            {/* Title and Subtitle */}
            <div
              className={`text-center mb-8 transition-all duration-500 transform ${
                showTitle ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <h2 className="text-[20px] font-bold text-white mb-3">
                {isPackage ? t('Purchase successful') : t('Upgrade completed!')}
              </h2>
              <p className="text-text-tertiary text-[14px]">
                {isPackage
                  ? t('Your plan is now active')
                  : t("You've unlocked Pythra's full research power")}
              </p>
            </div>

            {/* CTA Button */}
            <div
              className={`transition-all w-full flex items-center justify-center duration-500 transform ${
                showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <button
                onClick={onClose}
                className="w-[240px] py-4 px-6 text-black font-semibold rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg"
                style={{
                  background: 'linear-gradient(90deg, #67FFFF 0%, #A772FF 100%)',
                }}
              >
                {t("Let's get started!")}
              </button>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-12 h-12 bg-purple-500/20 rounded-full blur-xl" />
            <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-blue-500/20 rounded-full blur-xl" />
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessModal;
